import classNames from 'classnames';
import {ReactNode, useCallback, useEffect, useState} from 'react';
import {FCC} from 'types/react';

import {Text} from 'components/Text/Text';

import styles from './Tooltip.module.css';

export enum TooltipType {
    Default = 'Default',
    Error = 'Error',
}

export enum TooltipPosition {
    Top = 'Top',
    TopRight = 'TopRight',
    Bottom = 'Bottom',
}

export enum TooltipTheme {
    Light = 'Light',
    Dark = 'Dark',
}

export enum TooltipPointerPosition {
    Left = 'Left',
    Center = 'Center',
}

const TYPE_CLASS_NAMES = {
    [TooltipType.Default]: undefined,
    [TooltipType.Error]: styles.error,
};

const POSITION_CLASS_NAMES = {
    [TooltipPosition.Top]: styles.top,
    [TooltipPosition.Bottom]: styles.bottom,
    [TooltipPosition.TopRight]: styles.topRight,
};

const THEME_CLASS_NAMES = {
    [TooltipTheme.Light]: styles.light,
    [TooltipTheme.Dark]: styles.dark,
};

const POINTER_POSITION_CLASS_NAMES = {
    [TooltipPointerPosition.Left]: styles.pointerLeft,
    [TooltipPointerPosition.Center]: styles.pointerCenter,
};

type Props = {
    content: string | ReactNode;
    type?: TooltipType;
    visible?: boolean;
    className?: string;
    contentClassName?: string;
    position?: TooltipPosition;
    pointerPosition?: TooltipPointerPosition;
    theme?: TooltipTheme;
};

export const Tooltip: FCC<Props> = ({
    children,
    content,
    type = TooltipType.Default,
    visible,
    className,
    contentClassName,
    position = TooltipPosition.Top,
    theme = TooltipTheme.Dark,
    pointerPosition = TooltipPointerPosition.Center,
}) => {
    const [isVisible, setIsVisible] = useState(visible);
    const typeClassName = TYPE_CLASS_NAMES[type];
    const positionClassName = POSITION_CLASS_NAMES[position];
    const themeClassName = THEME_CLASS_NAMES[theme];
    const pointerPositionClassName =
        POINTER_POSITION_CLASS_NAMES[pointerPosition];

    useEffect(() => {
        if (visible !== undefined) {
            setIsVisible(visible);
        }
    }, [visible]);

    const handleMouseEnter = useCallback(() => {
        setIsVisible(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsVisible(false);
    }, []);

    return (
        <div
            className={classNames(styles.block, className)}
            onMouseEnter={visible === undefined ? handleMouseEnter : undefined}
            onMouseLeave={visible === undefined ? handleMouseLeave : undefined}
        >
            {children}
            {isVisible && (
                <div
                    className={classNames(
                        styles.tooltip,
                        typeClassName,
                        positionClassName,
                        pointerPositionClassName,
                        themeClassName,
                        contentClassName,
                    )}
                >
                    {typeof content === 'string' && (
                        <Text.Bold14>{content}</Text.Bold14>
                    )}
                    {typeof content !== 'string' && content}
                </div>
            )}
        </div>
    );
};
